import { useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Reveal, FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { SERVICES, mailto } from "@/data/site";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 pt-24" data-testid="service-not-found">
        <div className="text-center">
          <h1 className="font-display text-4xl font-semibold text-ink">Service not found</h1>
          <Link to="/services" data-testid="service-not-found-back-link" className="btn btn-primary mt-8">
            All services
          </Link>
        </div>
      </div>
    );
  }

  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`service-page-${slug}`}>
      <Seo
        title={`${service.title} | TecKube`}
        description={`${service.tagline} ${service.intro}`.slice(0, 158)}
      />
      <PageHero eyebrow="Our Services" title={service.title} lead={service.tagline} />

      <section className="bg-paper pb-24 md:pb-32" data-testid="service-detail-body">
        <div className="mx-auto max-w-7xl px-6">
          <FadeIn delay={0.1}>
            <div className="group overflow-hidden rounded-3xl border border-slate-200 shadow-[0_30px_70px_-36px_rgba(15,23,42,0.35)]">
              <img
                src={service.image}
                alt={service.title}
                className="aspect-[21/9] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03] md:aspect-[21/7]"
              />
            </div>
          </FadeIn>

          <div className="mt-16 grid gap-14 md:mt-24 md:grid-cols-12">
            <div className="md:col-span-7">
              <Reveal>
                <p className="text-xl leading-relaxed text-ink md:text-2xl">{service.intro}</p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-14 font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">
                  What we help with
                </h2>
                <ul className="mt-8">
                  {service.points.map((point, i) => (
                    <li
                      key={point}
                      className="flex items-start gap-4 border-t border-slate-200 py-5"
                      data-testid={`service-point-${slug}-${i}`}
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <Check className="h-3.5 w-3.5 text-primary" />
                      </span>
                      <span className="text-[17px] leading-relaxed text-soot">{point}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="md:col-span-4 md:col-start-9">
              <Reveal delay={0.15}>
                <div
                  className="noise sticky top-28 rounded-3xl bg-night p-8 text-white md:p-10"
                  data-testid="service-side-cta"
                >
                  <h3 className="font-display text-2xl font-semibold tracking-tight">
                    Let&rsquo;s discuss your project.
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-white/65">
                    Tell us about your project, your current environment and the challenges you
                    want to address.
                  </p>
                  <a
                    href={mailto(`TecKube — ${service.title}`)}
                    data-testid="service-discuss-mailto-button"
                    className="btn btn-light mt-8 w-full"
                  >
                    Discuss Your Project
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="mt-24 md:mt-32" data-testid="other-services">
            <Reveal>
              <div className="flex items-end justify-between gap-6">
                <h2 className="font-display text-2xl font-semibold tracking-tight text-ink md:text-3xl">
                  Other services
                </h2>
                <Link
                  to="/services"
                  data-testid="other-services-all-link"
                  className="group inline-flex items-center gap-2 font-display text-[15px] font-semibold text-primary"
                >
                  All services
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="mt-10 grid gap-5 md:grid-cols-3">
                {others.map((other, i) => (
                  <Reveal key={other.slug} delay={i * 0.07}>
                    <Link
                      to={`/services/${other.slug}`}
                      data-testid={`other-service-card-${other.slug}`}
                      className="spotlight group flex h-full flex-col justify-between rounded-3xl border border-slate-200 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]"
                      onMouseMove={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                      }}
                    >
                      <div>
                        <span className="font-mono text-xs text-primary">
                          {String(SERVICES.indexOf(other) + 1).padStart(2, "0")}
                        </span>
                        <h3 className="mt-4 font-display text-xl font-semibold leading-tight tracking-tight text-ink">
                          {other.title}
                        </h3>
                        <p className="mt-3 text-[15px] leading-relaxed text-soot">
                          {other.tagline}
                        </p>
                      </div>
                      <span className="mt-7 inline-flex items-center gap-2 font-display text-sm font-semibold text-primary">
                        Explore
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                      </span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
};

export default ServiceDetail;
